import { useParams } from 'react-router-dom'
import {
  useControlQuestions,
  useControlQuestionAnswer,
  useProtocolQuestionAnswerImages
} from '@services/protocols/protocols.hook'
import { SCALE_TYPE, COVERING_COST } from '@services/protocols/protocols.consts'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import {
  TextField,
  Box,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  IconButton,
  Typography,
  Badge
} from '@mui/material'
import ProtocolRoomImagesUpload from './ProtocolRoomImagesUpload'
import UploadProgress from '@components/UploadProgress'
import { uploadProgressStore } from '@services/uploadProgress/uploadProgress.store'
import { Add } from '@mui/icons-material'
import { useEffect, useState, forwardRef } from 'react'
import { useSnackbar } from 'notistack'
import Dialog from '@mui/material/Dialog'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import CloseIcon from '@mui/icons-material/Close'
import Slide from '@mui/material/Slide'
import { TransitionProps } from '@mui/material/transitions'
import SaveIcon from '@mui/icons-material/Save'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction='up' ref={ref} {...props} />
})

function ProtocolControlQuestions({
  roomInstance,
  isReadOnly,
  isOpen,
  handleClose
}: {
  roomInstance: { id: number; name: string; label: string }
  isReadOnly: boolean
  isOpen: boolean
  handleClose: () => void
}) {
  const theme = useTheme()
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'))
  const [selectedQuestion, setSelectedQuestion] = useState<number | null>(null)
  const { enqueueSnackbar } = useSnackbar()
  const { id } = useParams<{ id: string }>()
  const { controlQuestions, refetch, debitNoteTitlePattern } = useControlQuestions()
  const {
    createControlQuestionAnswer,
    controlQuestionAnswer,
    updateControlQuestionAnswer,
    refetch: refetchQuestionAnswer
  } = useControlQuestionAnswer(Number(id))
  const { createProtocolQuestionAnswerImages, deleteProtocolQuestionAnswerImages } =
    useProtocolQuestionAnswerImages(Number(id))

  const filteredControlQuestions = controlQuestions
    .filter((question) => question.roomType.name === roomInstance.name)
    .map((question) => {
      const answers = controlQuestionAnswer.filter((answer) => answer.question === question.id)
      return {
        ...question,
        answers
      }
    })

  const getAnswer = (answerId: number) => {
    const answer = controlQuestionAnswer.find(({ id }) => id === answerId)

    if (answer) {
      return {
        scale: answer.scale,
        comment: answer.comment,
        debitNotePosition: answer.debitNotePosition,
        coveringCost: answer.coveringCost,
        id: answer.id,
        images: answer.images
      }
    }

    return {
      scale: SCALE_TYPE.noIssue,
      comment: '',
      debitNotePosition: debitNoteTitlePattern[0].id,
      coveringCost: COVERING_COST.warranty,
      images: []
    }
  }

  const controlQuestionsForm = useFormik({
    validateOnMount: true,
    validateOnChange: true,
    initialValues: {
      questions: {
        ...filteredControlQuestions.reduce((acc, crr) => {
          const itemsToAdd = controlQuestionAnswer
            .filter(({ question, room }) => question === crr.id && room === roomInstance.id)
            .reduce((itemAcc, itemCrr, index) => {
              return {
                ...itemAcc,
                [`${index}_${crr.id}`]: getAnswer(itemCrr.id)
              }
            }, {} as any)
          return {
            ...acc,
            ...itemsToAdd
          }
        }, {} as any)
      }
    } as any,
    enableReinitialize: true,
    validationSchema: Yup.object().shape({
      questions: Yup.object().shape(
        filteredControlQuestions.reduce((acc, crr) => {
          const itemsToAdd = controlQuestionAnswer
            .filter(({ question, room }) => question === crr.id && room === roomInstance.id)
            .reduce((itemAcc, _itemCrr, index) => {
              return {
                ...itemAcc,
                [`${index}_${crr.id}`]: Yup.object().shape(
                  {
                    comment: Yup.string().when(['scale'], ([scale]) => {
                      if (scale === SCALE_TYPE.noIssue) {
                        return Yup.string().notRequired()
                      } else {
                        return Yup.string().required('To pole jest wymagane')
                      }
                    }),
                    debitNotePosition: Yup.number().when(['scale'], ([scale]) => {
                      if (scale === SCALE_TYPE.noIssue) {
                        return Yup.string().notRequired()
                      } else {
                        return Yup.string().required('To pole jest wymagane')
                      }
                    }),
                    coveringCost: Yup.string().when(['scale'], ([scale]) => {
                      if (scale === SCALE_TYPE.noIssue) {
                        return Yup.string().notRequired()
                      } else {
                        return Yup.string().required('To pole jest wymagane')
                      }
                    })
                  },
                  [
                    ['scale', 'coveringCost'],
                    ['scale', 'debitNotePosition'],
                    ['scale', 'comment']
                  ]
                )
              }
            }, {} as any)
          return {
            ...acc,
            ...itemsToAdd
          }
        }, {} as any)
      )
    }),
    onSubmit: async (values, form) => {
      for (const questionKey of Object.keys(values.questions)) {
        if (values.questions[questionKey].id) {
          //images create
          const images = values.questions[questionKey].images
          if (images.length) {
            let uploadIndex = 0
            const filesToUpload = images.filter(
              (image: File | string) => typeof image !== 'string' && (image as File).name
            ) as File[]

            for await (const image of images) {
              //if it is a file, not url string
              if (typeof image !== 'string' && image.name) {
                uploadIndex += 1
                uploadProgressStore.setMeta({
                  fileName: image.name,
                  fileIndex: uploadIndex,
                  totalFiles: filesToUpload.length,
                  estimatedTotal: image.size
                })
                await createProtocolQuestionAnswerImages.mutateAsync({
                  image: image,
                  controlQuestionAnswerId: values.questions[questionKey].id
                })
              }
            }
          }
          await updateControlQuestionAnswer.mutateAsync(values.questions[questionKey])
        } else {
          const newAnswer = await createControlQuestionAnswer.mutateAsync({
            id: Number(questionKey.split('_')[1]),
            ...values.questions[questionKey],
            room: roomInstance.id
          })

          // Check if we got a real response (online) or queued response (offline)
          const hasRealResponse = newAnswer && typeof newAnswer === 'object' && 'id' in newAnswer

          if (hasRealResponse) {
            // images create - only when online and we have real ID
            const images = values.questions[questionKey].images
            if (images.length) {
              let uploadIndex = 0
              const filesToUpload = images.filter(
                (image: File | string) => typeof image !== 'string' && (image as File).name
              ) as File[]

              for await (const image of images) {
                //if it is a file, not url string
                if (typeof image !== 'string' && image.name) {
                  uploadIndex += 1
                  uploadProgressStore.setMeta({
                    fileName: image.name,
                    fileIndex: uploadIndex,
                    totalFiles: filesToUpload.length,
                    estimatedTotal: image.size
                  })
                  await createProtocolQuestionAnswerImages.mutateAsync({
                    image: image,
                    controlQuestionAnswerId: newAnswer.id
                  })
                }
              }
            }
          }
        }
      }
      await refetchQuestionAnswer()
      await refetch()
      form.resetForm()
    }
  })

  const handleAddOtherIssue = (answerId: number) => {
    const normalAmount = Object.keys(controlQuestionsForm.values.questions).filter(
      (key) => answerId && key.split('_')[1] === answerId.toString()
    ).length

    controlQuestionsForm.setFieldValue(`questions`, {
      ...controlQuestionsForm.values.questions,
      [`${normalAmount}_${answerId}`]: {
        comment: '',
        coveringCost: 'Warranty',
        debitNotePosition: 3,
        scale: SCALE_TYPE.issue,
        images: []
      }
    })
  }
  useEffect(() => {
    if (Object.keys(controlQuestionsForm.errors).length) {
      enqueueSnackbar(
        `Błąd walidacji formularza. Sprawdź czy wszystkie pola wypełnione są poprawnie.`,
        {
          variant: 'bug' as any
        }
      )
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controlQuestionsForm.errors])

  useEffect(() => {
    if (!!roomInstance.id) {
      setSelectedQuestion(filteredControlQuestions[0]?.id ?? null)
    }
  }, [roomInstance])

  if (roomInstance.id === -1) {
    return null
  }

  const handleNextQuestion = async () => {
    await controlQuestionsForm.submitForm()

    // if there are no errors
    if (!Object.keys(controlQuestionsForm.errors).length) {
      const filteredControlQuestionsIds = filteredControlQuestions.map((question) => question.id)
      if (!!selectedQuestion) {
        const currentQuestionIndex = filteredControlQuestionsIds.indexOf(selectedQuestion)
        const nextQuestionIndex = currentQuestionIndex + 1
        if (nextQuestionIndex < filteredControlQuestionsIds.length) {
          setSelectedQuestion(filteredControlQuestionsIds[nextQuestionIndex])
        } else {
          setSelectedQuestion(filteredControlQuestionsIds[0])
        }
      } else {
        setSelectedQuestion(filteredControlQuestionsIds[0])
      }
    }
  }

  const handlePreviousQuestion = async () => {
    await controlQuestionsForm.submitForm()

    // if there are no errors
    if (!Object.keys(controlQuestionsForm.errors).length) {
      const filteredControlQuestionsIds = filteredControlQuestions.map((question) => question.id)
      if (!!selectedQuestion) {
        const currentQuestionIndex = filteredControlQuestionsIds.indexOf(selectedQuestion)
        const previousQuestionIndex = currentQuestionIndex - 1
        if (previousQuestionIndex >= 0) {
          setSelectedQuestion(filteredControlQuestionsIds[previousQuestionIndex])
        } else {
          setSelectedQuestion(filteredControlQuestionsIds[filteredControlQuestionsIds.length - 1])
        }
      } else {
        setSelectedQuestion(filteredControlQuestionsIds[0])
      }
    }
  }

  return (
    <Dialog fullScreen open={isOpen} onClose={() => null} TransitionComponent={Transition}>
      <AppBar position={isSmallScreen ? 'relative' : 'fixed'}>
        <Toolbar>
          <IconButton edge='start' color='inherit' onClick={handleClose} aria-label='close'>
            <CloseIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant='h6' component='div'>
            Pytania do pokoju: {roomInstance.label}
          </Typography>
          {!isReadOnly && (
            <Button
              autoFocus
              color='inherit'
              onClick={controlQuestionsForm.submitForm}
              startIcon={<SaveIcon />}
              disabled={controlQuestionsForm.isSubmitting}
              type='submit'
              loading={controlQuestionsForm.isSubmitting}
            >
              Zapisz
            </Button>
          )}
        </Toolbar>
      </AppBar>
      <Box
        sx={(theme) => ({
          py: 10,
          [theme.breakpoints.down('sm')]: {
            pt: 2
          }
        })}
      >
        <form onSubmit={controlQuestionsForm.handleSubmit}>
          <>
            <Box
              sx={(theme) => ({
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                mb: 2,
                [theme.breakpoints.down('sm')]: {
                  flexWrap: 'nowrap',
                  overflowX: 'auto',
                  px: 1,
                  mb: 0,
                  pb: 2
                }
              })}
            >
              {filteredControlQuestions.map((question) => {
                return (
                  <Badge
                    invisible={!question.answers.length}
                    key={question.id}
                    badgeContent={question.answers.length}
                    color='error'
                    sx={{ width: 'fit-content' }}
                  >
                    <Button
                      variant={selectedQuestion === question.id ? 'contained' : 'outlined'}
                      onClick={() => setSelectedQuestion(question.id)}
                    >
                      {question.question}
                    </Button>
                  </Badge>
                )
              })}
            </Box>
          </>
          {filteredControlQuestions.map((question, index) => (
            <Box
              key={index}
              sx={(theme) => ({
                display: selectedQuestion === question.id ? 'flex' : 'none',
                flexDirection: 'column',
                px: 4,
                [theme.breakpoints.down('sm')]: {
                  px: 1
                }
              })}
            >
              <FormLabel
                sx={{
                  margin: '1rem 0',
                  fontSize: '.9rem',
                  textTransform: 'uppercase',
                  fontWeight: 'bold'
                }}
              >
                {question.question}
              </FormLabel>
              {Object.keys(controlQuestionsForm.values.questions)
                .filter((item) => item.split('_')[1] === question.id?.toString())
                .map((key) => (
                  <Box
                    key={key}
                    mb={2}
                    sx={{ borderTop: '1px solid #E0E0E0', borderBottom: '1px solid #E0E0E0' }}
                  >
                    <FormControl fullWidth sx={{ my: 2 }}>
                      <RadioGroup
                        defaultValue='scale'
                        value={controlQuestionsForm.values.questions[key]?.scale}
                        onChange={({ target }) => {
                          controlQuestionsForm.setFieldValue(
                            `questions[${key}].scale`,
                            target.value
                          )
                        }}
                      >
                        {Object.values(SCALE_TYPE).map((scaleType) => (
                          <FormControlLabel
                            key={scaleType}
                            value={scaleType}
                            control={<Radio />}
                            label={scaleType}
                            disabled={controlQuestionsForm.isSubmitting || isReadOnly}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                    {controlQuestionsForm.values.questions[key]?.scale === SCALE_TYPE.issue && (
                      <>
                        <TextField
                          fullWidth
                          label='Komentarz'
                          sx={{ mb: 2 }}
                          value={controlQuestionsForm.values.questions[key].comment}
                          name={`questions.${key}.comment`}
                          onChange={controlQuestionsForm.handleChange}
                          error={
                            controlQuestionsForm.errors.questions
                              ? !!(controlQuestionsForm.errors.questions as any)[key]?.comment
                              : false
                          }
                          helperText={
                            controlQuestionsForm.errors.questions
                              ? (controlQuestionsForm.errors.questions as any)[key]?.comment
                              : ''
                          }
                          disabled={controlQuestionsForm.isSubmitting || isReadOnly}
                        />
                        <Autocomplete
                          value={debitNoteTitlePattern
                            .map(({ id, title }) => ({
                              label: title,
                              value: id
                            }))
                            .find(
                              ({ value }) =>
                                value ===
                                controlQuestionsForm.values.questions[key].debitNotePosition
                            )}
                          onChange={(_, value: any) => {
                            controlQuestionsForm.setFieldValue(
                              `questions[${key}].debitNotePosition`,
                              value?.value
                            )
                          }}
                          fullWidth
                          disablePortal
                          id='debitNotePosition'
                          options={debitNoteTitlePattern.map(({ id, title }) => ({
                            label: title,
                            value: id
                          }))}
                          getOptionKey={(option) => option.value}
                          isOptionEqualToValue={(option, value) => option.value === value.value}
                          disabled={controlQuestionsForm.isSubmitting || isReadOnly}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label='Nota obciążeniowa'
                              name='debitNotePosition'
                              fullWidth
                            />
                          )}
                        />
                        <FormControl fullWidth sx={{ my: 2 }}>
                          <InputLabel id='coveringCost'>Pokrycie kosztów</InputLabel>
                          <Select
                            disabled={controlQuestionsForm.isSubmitting || isReadOnly}
                            labelId='coveringCost'
                            value={controlQuestionsForm.values.questions[key].coveringCost}
                            label='Pokrycie kosztów'
                            onChange={({ target }) =>
                              controlQuestionsForm.setFieldValue(
                                `questions[${key}].coveringCost`,
                                target.value
                              )
                            }
                          >
                            {Object.values(COVERING_COST).map((coveringCost) => (
                              <MenuItem key={coveringCost} value={coveringCost}>
                                {coveringCost}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <ProtocolRoomImagesUpload
                          multiple
                          disabled={controlQuestionsForm.isSubmitting || isReadOnly}
                          roomId={roomInstance.id}
                          onAdd={(images) => {
                            controlQuestionsForm.setFieldValue(`questions[${key}].images`, [
                              ...controlQuestionsForm.values.questions[key].images,
                              ...(images as File[])
                            ])
                          }}
                          onRemove={async (imageKey) => {
                            controlQuestionsForm.setFieldValue(
                              `questions[${key}].images`,
                              controlQuestionsForm.values.questions[key].images.filter(
                                (_: File | string, k: number) => k !== imageKey
                              )
                            )
                            const image = controlQuestionsForm.values.questions[key].images.find(
                              (_: File | string, index: number) => index === imageKey
                            )
                            if (image?.id) {
                              await deleteProtocolQuestionAnswerImages.mutateAsync({
                                controlQuestionAnswerId:
                                  controlQuestionsForm.values.questions[key].id,
                                keyId: image.id
                              })
                            }
                          }}
                          images={
                            controlQuestionsForm.values.questions[key].images?.map(
                              (image: File | string) => {
                                return (image as any).image || image
                              }
                            ) || []
                          }
                        />
                      </>
                    )}
                  </Box>
                ))}
              {!isReadOnly && (
                <Button
                  onClick={() => handleAddOtherIssue(question.id)}
                  color='secondary'
                  size='large'
                  variant='contained'
                  startIcon={<Add />}
                  sx={{ width: 'fit-content', mb: 2 }}
                >
                  {`Dodaj usterkę (${question.question})`}
                </Button>
              )}
            </Box>
          ))}
        </form>
        <UploadProgress sx={{ mx: 2, mb: 10 }} />
      </Box>
      <AppBar
        position='fixed'
        color='transparent'
        sx={{ top: 'auto', bottom: 0, backgroundColor: 'white' }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            autoFocus
            color='inherit'
            onClick={handlePreviousQuestion}
            startIcon={<KeyboardArrowLeftIcon />}
            disabled={controlQuestionsForm.isSubmitting}
            type='button'
            loading={controlQuestionsForm.isSubmitting}
          >
            Poprzednie pytanie
          </Button>
          <Button
            autoFocus
            color='inherit'
            onClick={handleNextQuestion}
            endIcon={<KeyboardArrowRightIcon />}
            disabled={controlQuestionsForm.isSubmitting}
            type='button'
            loading={controlQuestionsForm.isSubmitting}
          >
            Nastepne pytanie
          </Button>
        </Toolbar>
      </AppBar>
    </Dialog>
  )
}

export default ProtocolControlQuestions
